import React from 'react';
import './style.css';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const TwoLineChart = ({
    noImprovementsTitle,
    noImprovementsData,
    withImprovementsTitle,
    withImprovementsData,
    labels,
    chartTitle,
    roundTo = 5000,
    negativeMinAllowed = false,
    deviceType = 'desktop',
    chartType = 'default'
}) => {

    // Calculate growth rates
    const calculateGrowthRate = (data) => {
        const firstValue = data[0];
        const lastValue = data[data.length - 1];
        const years = data.length - 1;
        
        // Calculate compound annual growth rate (CAGR)
        const growthRate = ((lastValue / firstValue) ** (1 / years) - 1) * 100;
        return growthRate.toFixed(1); // Round to 1 decimal place
    };

    const noImprovementsGrowth = calculateGrowthRate(noImprovementsData);
    const withImprovementsGrowth = calculateGrowthRate(withImprovementsData);

    const noImprovementsLabel = chartType !== 'budgeting' ? `${noImprovementsTitle} (Growth Rate: ${noImprovementsGrowth}%)` : `${noImprovementsTitle}`;
    const withImprovementsLabel = chartType !== 'budgeting' ? `${withImprovementsTitle} (Growth Rate: ${withImprovementsGrowth}%)` : `${withImprovementsTitle}`;

    const chartData = {
        labels,
        datasets: [
            {
                label: noImprovementsLabel,
                data: noImprovementsData,
                borderColor: '#FF6B6B',  // Red line
                backgroundColor: '#FF6B6B',
                pointRadius: 4,
                tension: 0.1
            },
            {
                label: withImprovementsLabel,
                data: withImprovementsData,
                borderColor: '#0D5257',  // Dark teal
                backgroundColor: '#0D5257',
                pointRadius: 4,
                tension: 0.1
            }
        ]
    };

    // Function to calculate axis bounds
    const calculateYAxisBounds = () => {
        // Combine all data points
        const allValues = [...noImprovementsData, ...withImprovementsData];
        
        // Get min and max values
        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        
        // Add 10% padding to the range
        const range = maxValue - minValue;
        const padding = range * 0.1;
        
        // Round to nearest roundTo value for cleaner numbers
        // Only ensure min is never below 0 if negativeMinAllowed is false
        const min = negativeMinAllowed 
            ? Math.floor((minValue - padding) / roundTo) * roundTo
            : Math.max(0, Math.floor((minValue - padding) / roundTo) * roundTo);
        const max = Math.ceil((maxValue + padding) / roundTo) * roundTo;
        
        // Calculate appropriate step size based on range
        const totalRange = max - min;
        const stepSize = Math.ceil(totalRange / 6 / roundTo) * roundTo; // Aim for ~6 steps

        return { min, max, stepSize };
    };

    const { min, max, stepSize } = calculateYAxisBounds();

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: deviceType === 'mobile' ? 1 : 2,
        plugins: {
            ...(chartTitle && {
                title: {
                    display: true,
                    text: chartTitle,
                    font: {
                        size: 12,
                        weight: 'bold'
                    },
                    padding: {
                        bottom: 30
                    }
                }
            }),
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        weight: 'bold'
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                            }).format(context.parsed.y);
                        }
                        return label;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: false,
                min: min,
                max: max,
                ticks: {
                    stepSize: stepSize,
                    callback: function (value) {
                        return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            notation: 'compact',
                            compactDisplay: 'short'
                        }).format(value);
                    }
                },
                grid: {
                    drawBorder: true,
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };



    return (
        <>
            {/* <div className="chart-container"> */}
                <Line data={chartData} options={options} />
            {/* </div> */}
        </>

    );
};

export default TwoLineChart;

